import { isEmpty, isEqual } from 'radash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { useAttributesStateStore } from '@/components/datamodel/hooks/useAttributesStore';
import { useValidationsStore, ValidationStatus } from '@/components/datamodel/hooks/useValidationsStore';
import type { BaseValidation } from '@/components/datamodel/types';
import { useValidationWorker } from '@/dialogs/item-editor/hooks/validation/useValidationWorker';
import { prepareValidationOperations } from '@/dialogs/item-editor/hooks/validation/validation.shared';
import {
    handleValidationError,
    handleValidationSuccess,
} from '@/dialogs/item-editor/hooks/validation/validationResponseHandlers';
import { useDataModel } from '@/hooks/useDataModel';
import { useItemChangesQueue } from '@/hooks/useItemChangesQueue';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { validateDamAsset } from '@/pages/dam/api/damAssetValidation';
import { DataModelAttributeTypeName } from '@/types/DataModel';

type AssetValues = Record<string, unknown>;

/** Debounce and immediate types of the item editor (`useValidations`), so both editors behave alike (D-051). */
const VALIDATION_DELAY_MS = 3000;

const IMMEDIATE_VALIDATION_TYPES = new Set<DataModelAttributeTypeName>([
    DataModelAttributeTypeName.Boolean,
    DataModelAttributeTypeName.Enum,
    DataModelAttributeTypeName.EnumSet,
    DataModelAttributeTypeName.OpenEnum,
    DataModelAttributeTypeName.OpenEnumSet,
    DataModelAttributeTypeName.Codelist,
    DataModelAttributeTypeName.CodelistSet,
    DataModelAttributeTypeName.OpenCodelist,
    DataModelAttributeTypeName.OpenCodelistSet,
]);

/** Editors report absent values as `''` or `[]`; those are not changes. */
const normalizeBlank = (value: unknown) =>
    (value === undefined || value === null || (typeof value !== 'number' && typeof value !== 'boolean' && isEmpty(value)) ? null : value);

const isSameValue = (a: unknown, b: unknown) => isEqual(normalizeBlank(a), normalizeBlank(b));

interface UseDamLiveValidationOptions {
    /** The stored asset; its `primaryKey__` and `category__` go with every request. */
    asset: AssetValues;
    /** Attributes the user can change; only they are sent as changes. */
    attributeNames: string[];
    /** Validation of the stored asset with full messages (`__validations__`), shown until the first live result. */
    initialValidations?: Record<string, BaseValidation[]> | null;
    /** Attribute states of the stored asset (`__attributeStates__`). */
    initialAttributeStates?: Record<string, string[]> | null;
}

/**
 * Live validation of the asset data editor, like the item editor: every change is validated
 * against `POST /api/v2/assets/validate` after 3 s, option lists at once; messages go to
 * `useValidationsStore`, hidden / read-only states to `useAttributesStateStore`, calculated
 * values into the form. Must be used inside the editor's `FormProvider`.
 */
function useDamLiveValidation({ asset, attributeNames, initialValidations, initialAttributeStates }: UseDamLiveValidationOptions) {
    const { watch, setValue, getValues } = useFormContext();
    const { dataModel } = useDataModel();
    const { toast } = useToast();
    const { t } = useTranslation();
    const workerRef = useValidationWorker();
    const calculatedAttributesRef = useRef<Set<string>>(new Set());
    const updateItemRef = useRef<null | ((attributeName: string, value: unknown) => void)>(null);

    const { setStatus, applyServerValidations, clearErrors, clearWarnings } = useValidationsStore(useShallow((state) => ({
        setStatus: state.setStatus,
        applyServerValidations: state.applyServerValidations,
        clearErrors: state.clearErrors,
        clearWarnings: state.clearWarnings,
    })));
    const { applyAttributeStates, clearState } = useAttributesStateStore(useShallow((state) => ({
        applyAttributeStates: state.applyAttributeStates,
        clearState: state.clearState,
    })));

    const primaryKey = asset.primaryKey__;
    const category = asset.category__;
    const editable = useMemo(() => new Set(attributeNames), [attributeNames]);

    const setValidationsOnAttributes = useCallback(async (validations: Record<string, BaseValidation[]>, validatedAttributeNames?: string[]) => {
        const worker = workerRef.current;
        const operations = worker
            ? await worker.prepareValidationOperations({ validations, validatedAttributeNames })
            : prepareValidationOperations({ validations, validatedAttributeNames });
        applyServerValidations(operations);
    }, [applyServerValidations, workerRef]);

    const updateStatus = useCallback(() => {
        const { validationErrors } = useValidationsStore.getState();
        setStatus(Object.keys(validationErrors ?? {}).length > 0 ? ValidationStatus.IS_NOT_COMPLIANT : ValidationStatus.IS_COMPLIANT);
    }, [setStatus]);

    const { start, stop, enqueueChange, flush, updateProcessed, isProcessing } = useItemChangesQueue<AssetValues>({
        defaultDelayMs: VALIDATION_DELAY_MS,
        isEqual: isSameValue,
        processor: async ({ patch, processedItem, processingItem }) => {
            setStatus(ValidationStatus.IS_RUNNING);
            const variables = { attributeNames: Object.keys(patch), attributeStatesOnly: false };
            try {
                const response = await validateDamAsset(
                    { ...processingItem, primaryKey__: primaryKey, category__: category },
                    { ...variables, oldAsset: { ...processedItem, primaryKey__: primaryKey, category__: category } },
                );
                await handleValidationSuccess(
                    { ...response, calculations: response.calculations ?? {} },
                    variables,
                    updateItemRef.current,
                    getValues,
                    calculatedAttributesRef,
                    applyAttributeStates,
                    setValidationsOnAttributes,
                );
                updateStatus();
            } catch (error) {
                handleValidationError(error, variables, setStatus, toast, t);
                throw error;
            }
        },
    });

    useEffect(() => {
        // A calculated value goes into the last validated snapshot first, so setting it does not trigger a validation.
        updateItemRef.current = (attributeName, value) => {
            updateProcessed(attributeName, value);
            setValue(attributeName, value);
        };
    }, [setValue, updateProcessed]);

    useEffect(() => {
        const values = getValues();
        start(Object.fromEntries(attributeNames.map((name) => [name, values[name] ?? null])));
        return () => {
            stop();
        };
        // The queue starts once per editor; later value changes arrive through `watch`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (initialAttributeStates) {
            applyAttributeStates(initialAttributeStates);
        }
        if (initialValidations) {
            void setValidationsOnAttributes(initialValidations).then(updateStatus);
        }
    }, [initialValidations, initialAttributeStates, applyAttributeStates, setValidationsOnAttributes, updateStatus]);

    useEffect(() => () => {
        clearErrors();
        clearWarnings();
        clearState();
    }, [clearErrors, clearWarnings, clearState]);

    useEffect(() => {
        const subscription = watch((values, { name }) => {
            const attributeName = name?.split('.')[0];
            if (!attributeName || !editable.has(attributeName)) {
                return;
            }
            const value = (values as AssetValues)[attributeName];
            enqueueChange({ [attributeName]: value === undefined ? null : value });
            const typeName = dataModel?.attribute(attributeName)?.typeName;
            if (typeName && IMMEDIATE_VALIDATION_TYPES.has(typeName)) {
                flush();
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, editable, enqueueChange, flush, dataModel]);

    return { isValidating: isProcessing, flush };
}

export { useDamLiveValidation, VALIDATION_DELAY_MS };
export type { UseDamLiveValidationOptions };
